'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Popover,
} from 'antd';
import useAppMessage from '../../hooks/useAppMessage';
import { FilterOutlined } from '@ant-design/icons';
import FilterCard from './FilterCard';
import GroupCard from './GroupCard';
import SortCard from './SortCard';
import SortIcon from '../icons/SortIcon';
import { addViewToMenu } from '../../utils/menuManager';
import { initialFilterState, normalizeFieldName } from '../../constants/filterConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import CreateTaskModal from '../modals/CreateTaskModal';
import ImportTaskModal from '../modals/ImportTaskModal';
import useStyles from '../../styles/components/filter/filter-system';
import './filter-system.css';

/**
 * 筛选系统组件，处理API规范中的筛选、排序功能
 * @param {Object} props
 * @param {Function} props.onFilterChange - 筛选变更回调
 * @param {Fun  ction} props.onSortChange - 排序变更回调
 * @param {Function} props.onViewChange - 视图变更回调
 * @param {Function} props.onImportSuccess - 导入成功回调
 * @param {Function} props.onGenerateReport - 生成报告回调
 * @param {Object} props.currentFilter - 当前筛选条件
 * @param {Object} props.currentSort - 当前排序条件
 * @param {Object} props.currentView - 当前视图
 * @param {Object} props.currentGroup - 当前分组条件
 */
const FilterSystem = ({
  onFilterChange,
  onSortChange,
  onViewChange,
  onImportSuccess,
  onGenerateReport,
  currentFilter,
  currentSort,
  currentView,
  currentGroup,
}) => {
  const { styles } = useStyles();
  // 全局消息
  const { message, contextHolder } = useAppMessage();
  // 状态管理
  const [state, setState] = useState({
    ...initialFilterState,
    activePopover: null, // 'filter', 'group', 'sort' 或 null
    searchValue: '', // 添加搜索值状态
  });
  const [loading, setLoading] = useState(false);
  // 为弹窗延后显示增加一个"待显示"标记
  const [showViewportModal, setShowViewportModal] = useState(false);
  const [pendingViewportPrompt, setPendingViewportPrompt] = useState(false);
  const [isCreateTaskModalVisible, setIsCreateTaskModalVisible] =
    useState(false);
  const [isImportTaskModalVisible, setIsImportTaskModalVisible] =
    useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const buttonContainerRef = useRef(null);


  // 路由信息
  const isTaskPage = location.pathname === '/tasks' 
  // 是否从子菜单加载的已保存视图页面
  const isSubmenuPage = new URLSearchParams(location.search).has('view_id');

  // 生成报告模态框状态

  // 判断是否为提案广场页
  const isProposalSquarePage = location.pathname === '/explore/proposals';
  
  // 初始化时，如果有外部传入的筛选或排序条件，更新状态
  useEffect(() => {
    if (currentFilter) {
      try {
        // 处理不同格式的筛选条件
        let processedFilter = currentFilter;

        // 如果是数组格式，需要处理成标准格式
        if (Array.isArray(currentFilter)) {
          const mergedExprs = [];
          currentFilter.forEach((filter) => {
            if (filter.exprs && Array.isArray(filter.exprs)) {
              mergedExprs.push(...filter.exprs);
            }
          });

          if (mergedExprs.length > 0) {
            processedFilter = { exprs: mergedExprs };
          }
        }

        // 转换为UI条件并更新状态
        const uiConditions = convertApiFilterToUiFilter(processedFilter);

        setState((prev) => ({
          ...prev,
          filterConfig: {
            ...prev.filterConfig,
            conditions: uiConditions,
          },
        }));
      } catch (error) {
        console.error('处理筛选条件出错:', error);
      }
    } else if (currentFilter === null) {

      setState((prev) => ({
        ...prev,
        filterConfig: {
          ...prev.filterConfig,
          conditions: [],
        },
      }));
    }
  }, [currentFilter]);

  useEffect(() => {
    if (currentSort) {
      setState((prev) => ({
        ...prev,
        sortConfig: {
          ...prev.sortConfig,
          fields: [
            {
              id: `${currentSort.field}_${currentSort.desc ? 'desc' : 'asc'}`,
              field: currentSort.field,
              direction: currentSort.desc ? 'desc' : 'asc',
            },
          ],
        },
      }));
    } else if (currentSort === null) {
      // 如果currentSort为null，清空排序条件
      setState((prev) => ({
        ...prev,
        sortConfig: {
          ...prev.sortConfig,
          fields: [],
        },
      }));
    }
  }, [currentSort]);

  // 处理 currentGroup 参数的 useEffect
  useEffect(() => {
    if (currentGroup) {
      setState((prev) => ({
        ...prev,
        groupConfig: {
          ...prev.groupConfig,
          fields: currentGroup.fields ? currentGroup.fields.map((field, index) => ({
            id: `group${index + 1}`,
            field: field,
          })) : [],
        },
      }));
    } else if (currentGroup === null) {
      // 清空分组条件
      setState((prev) => ({
        ...prev,
        groupConfig: {
          ...prev.groupConfig,
          fields: [],
        },
      }));
    }
  }, [currentGroup]);

  // 将API筛选条件转换为UI筛选条件
  const convertApiFilterToUiFilter = (apiFilter) => {
    if (!apiFilter || !apiFilter.exprs) {
      return [];
    }

    // 将API筛选表达式转换为UI筛选条件
    return apiFilter.exprs.map((expr, index) => {
      // 标准化字段名称
      const normalizedField = normalizeFieldName(expr.field);

      // 确定UI操作符
      let operator;
      switch (expr.op) {
        case 'EQ':
          operator = '等于';
          break;
        case 'NEQ':
          operator = '不等于';
          break;
        case 'GT':
          operator = '大于';
          break;
        case 'GTE':
          operator = '大于等于';
          break;
        case 'LT':
          operator = '小于';
          break;
        case 'LTE':
          operator = '小于等于';
          break;
        case 'LIKE':
          operator = '包含';
          break;
        case 'NOT_IN':
          operator = '不包含';
          break;
        case 'RANGE':
          operator = '在范围内';
          break;
        case 'IS_NULL':
          operator = '为空';
          break;
        case 'IS_NOT_NULL':
          operator = '不为空';
          break;
        default:
          operator = '等于';
      }

      // 确保values是数组且有值，但"为空"和"不为空"操作符不需要值
      const values =
        operator === '为空' || operator === '不为空'
          ? []
          : Array.isArray(expr.values)
          ? expr.values
          : expr.values
          ? [expr.values]
          : [];

      // 返回UI格式的筛选条件，同时包含value和values属性
      return {
        id: `filter${index + 1}`,
        field: normalizedField,
        operator,
        value: values.length === 1 ? values[0] : values, // 保留value属性以兼容旧代码
        values: values, // 同时设置values属性
      };
    });
  };

  // 将UI筛选条件转换为API筛选条件
  const convertUiFilterToApiFilter = (uiFilter) => {
    if (!uiFilter || uiFilter.length === 0) return null;

    // 创建API规范的FilterList结构
    const filterList = {
      exprs: [],
    };

    // 将UI筛选条件转换为API筛选表达式
    uiFilter.forEach((condition) => {
      // 标准化字段名称
      const fieldName = normalizeFieldName(condition.field);

      // 特殊处理"为空"和"不为空"操作符
      if (condition.operator === '为空' || condition.operator === '不为空') {
        // 这两个操作符不需要值，可以直接创建表达式
        const op = condition.operator === '为空' ? 'IS_NULL' : 'IS_NOT_NULL';
        filterList.exprs.push({
          field: fieldName,
          op,
          values: [], // 为空操作符不需要值
        });
        return; // 处理完当前条件，继续下一个
      }

      // 确定操作符类型
      let op;
      switch (condition.operator) {
        case '等于':
          op = 'EQ';
          break;
        case '不等于':
          op = 'NEQ';
          break;
        case '大于':
          op = 'GT';
          break;
        case '大于等于':
          op = 'GTE';
          break;
        case '小于':
          op = 'LT';
          break;
        case '小于等于':
          op = 'LTE';
          break;
        case '包含':
          op = 'LIKE';
          break;
        case '不包含':
          op = 'NOT_IN';
          break;
        case '在范围内':
          op = 'RANGE';
          break;
        default:
          op = 'EQ';
      }

      // 确保有值（除了"为空"和"不为空"操作符外，其他操作符需要值）
      const values = Array.isArray(condition.values)
        ? condition.values
        : condition.value !== undefined
        ? [condition.value]
        : [];

      // 即使没有值也添加筛选条件，避免条件丢失
      filterList.exprs.push({
        field: fieldName,
        op,
        values: values,
      });
    });

    return filterList.exprs.length > 0 ? filterList : null;
  };

  // 显示指定的弹窗
  const showPopover = (popoverType) => {
    setState((prevState) => ({
      ...prevState,
      step: popoverType, // 设置当前步骤为弹窗类型
      activePopover: popoverType, // 设置活动弹窗
    }));
  };

  // 关闭弹窗
  const closePopover = () => {
    setState((prevState) => ({
      ...prevState,
      activePopover: null,
    }));
    // 关闭 popover 的同时，如果有待显示标记则真正弹出提示
    if (pendingViewportPrompt) {
      setShowViewportModal(true);
      setPendingViewportPrompt(false);
    }
  };

  // 步骤控制
  const handleStepChange = (direction) => {
    setState((prevState) => {
      const currentStep = prevState.step;
      let nextStep = currentStep;

      if (direction === 'next') {
        if (currentStep === 'filter') nextStep = 'sort';
        // else if (currentStep === 'group') nextStep = 'sort';
      } else if (direction === 'prev') {
        if (currentStep === 'sort') nextStep = 'filter';
        // else if (currentStep === 'group') nextStep = 'filter';
      }

      return {
        ...prevState,
        step: nextStep,
        activePopover: nextStep, // 更新活动弹窗
      };
    });
  };

  // 更新筛选配置
  const updateFilterConfig = (config) => {
    setState((prev) => ({ ...prev, filterConfig: config }));
    // 只打标记，不马上显示
    if (!pendingViewportPrompt) {
      setPendingViewportPrompt(true);
    }
    if (onFilterChange) {
      const apiFilter = convertUiFilterToApiFilter(config.conditions);
      onFilterChange(apiFilter);
    }
  };

  // 更新分组配置
  const updateGroupConfig = (config) => {
    setState((prev) => ({ ...prev, groupConfig: config }));
    if (!pendingViewportPrompt) {
      setPendingViewportPrompt(true);
    }
  };

  // 更新排序配置
  const updateSortConfig = (config) => {
    setState((prev) => ({ ...prev, sortConfig: config }));
    if (!pendingViewportPrompt) {
      setPendingViewportPrompt(true);
    }
    if (onSortChange) {
      if (config.fields.length > 0) {
        const f = config.fields[0];
        onSortChange({ field: f.field, desc: f.direction === 'desc' });
      } else {
        onSortChange(null); // 修复：无排序时传 null
      }
    }
  };

  // 处理搜索值变化
  const handleSearchChange = (e) => {
    setState((prevState) => ({
      ...prevState,
      searchValue: e.target.value,
    }));
  };

  // 处理搜索提交
  const handleSearch = (value) => {
    const searchText = value || state.searchValue;

    // 如果有搜索文本，创建一个"包含"筛选条件
    if (searchText && onFilterChange) {
      const searchFilter = {
        exprs: [
          {
            field: 'keyword', // 使用关键词字段进行搜索
            op: 'LIKE',
            values: [searchText],
          },
        ],
      };
      onFilterChange(searchFilter);
    }


  };

  // 显示创建任务模态框
  const showCreateTaskModal = () => {
    setIsCreateTaskModalVisible(true);
  };

  // 隐藏创建任务模态框
  const handleModalCancel = () => {
    setIsCreateTaskModalVisible(false);
  };

  // 显示导入任务模态框
  const showImportTaskModal = () => {
    setIsImportTaskModalVisible(true);
  };

  // 隐藏导入任务模态框
  const handleImportModalCancel = () => {
    setIsImportTaskModalVisible(false);
  };

  // 导入任务成功后的回调
  const handleImportSuccess = () => {
    // 如果父组件提供了onImportSuccess回调，则使用父组件的回调
    if (onImportSuccess) {
      onImportSuccess();
    }
    // 否则使用旧的方式，通过onFilterChange触发数据刷新
    else if (onFilterChange) {
      onFilterChange(null); // 清空筛选条件，重新加载全部数据
    }
  };

  // 获取当前视图类型
  const getViewType = () => {
    return isTaskPage ? 'task' : 'exploration';
  };

  // 刷新当前页面的菜单数据，而不刷新整个页面
  const refreshMenuData = () => {
    // 获取当前路径
    const currentPath = location.pathname;

    // 重新导航到当前路径，并传递特殊标志以保留当前筛选条件
    navigate(currentPath, {
      state: {
        refreshMenu: true,
        preserveFilters: true,
        filterParams: currentFilter,
        sortParams: currentSort,
      },
      replace: true, // 替换当前历史记录，不创建新的历史记录
    });
  };

  // 修改保存视图配置的处理函数
  const handleSave = async () => {
    try {
      setLoading(true);
      const { filterConfig, groupConfig, sortConfig } = state;

      // 将UI配置转换为API格式
      const apiFilter = convertUiFilterToApiFilter(filterConfig.conditions);

      // 构建排序参数
      let apiSort = null;
      if (sortConfig.fields.length > 0) {
        const sortField = sortConfig.fields[0];
        apiSort = {
          field: sortField.field,
          desc: sortField.direction === 'desc',
        };
      }

      // 调用父组件的回调函数
      if (onFilterChange) {
        onFilterChange(apiFilter);
      }

      if (onSortChange) {
        onSortChange(apiSort || { field: 'inserted_at', desc: true });
      }

      // 添加 groupConfig 的处理
      const apiGroup = groupConfig.fields.length > 0 ? {
        fields: groupConfig.fields.map(field => field.field)
      } : null;

      // 将视图保存到菜单
      const viewType = isTaskPage ? 'task' : 'exploration';
      const viewData = {
        filterParams: apiFilter,
        groupParams: apiGroup,
        sortParams: apiSort,
      };

      const result = addViewToMenu(viewType, viewData, location.pathname);

      window.message?.success(`视图已保存为"${result.menuItem.title}"`);
      // 保存成功后不关闭popover，保持用户操作状态

      // 不需要刷新整个菜单，菜单变更事件会自动触发菜单刷新
      // 原有的refreshMenuData()方法不再需要调用
    } catch (error) {
      console.error('保存视图失败:', error);
      window.message?.error(error.message || '保存视图配置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 新增：保存修改到原视图
  const handleSaveOriginal = () => {
    const data = localStorage.getItem('syntrust_menu_data');
    const menuData = JSON.parse(data);
    const items = isTaskPage ? menuData[1].children : menuData[0].children;
    const currentView = items.find(
      (item) => item.id === parseInt(location.search.split('view_id=')[1])
    );
    if (currentView.viewData) {
      // 更新当前视图的筛选和排序参数
      currentView.viewData.filterParams = convertUiFilterToApiFilter(
        state.filterConfig.conditions
      );
      
      // 添加 groupParams 的更新
      currentView.viewData.groupParams = state.groupConfig.fields.length > 0 ? {
        fields: state.groupConfig.fields.map(field => field.field)
      } : null;
      
      currentView.viewData.sortParams =
        state.sortConfig.fields.length > 0
          ? {
              field: state.sortConfig.fields[0].field,
              desc: state.sortConfig.fields[0].direction === 'desc',
            }
          : null;

      // 更新菜单数据
      localStorage.setItem('syntrust_menu_data', JSON.stringify(menuData));

      window.message?.success('视图已更新');
      // 更新成功后不关闭popover，保持用户操作状态
    } else {
      window.message?.error('未找到当前视图，请重试');
    }
  };

  // 获取筛选条件数量
  const getFilterCount = () => {
    return state.filterConfig.conditions.length;
  };

  // 获取分组条件数量
  const getGroupCount = () => {
    return state.groupConfig.fields.length;
  };

  // 获取排序条件数量
  const getSortCount = () => {
    return state.sortConfig.fields.length;
  };

  // 筛选卡片内容
  const filterCardContent = (
    <div className="filter-card ">
      <FilterCard
        config={state.filterConfig}
        onConfigChange={updateFilterConfig}
        onNext={() => handleStepChange('next')}
      />
    </div>
  );

  // 分组卡片内容
  const groupCardContent = (
    <div className="filter-card">
      <GroupCard
        config={state.groupConfig}
        onConfigChange={updateGroupConfig}
        onPrev={() => handleStepChange('prev')}
        onNext={() => handleStepChange('next')}
      />
    </div>
  );

  // 排序卡片内容
  const sortCardContent = (
    <div className="filter-card">
      <SortCard
        config={state.sortConfig}
        onConfigChange={updateSortConfig}
        onPrev={() => handleStepChange('prev')}
        onSave={handleSave}
        loading={loading}
      />
    </div>
  );

  return (
    <div className={styles.filterSystemContainer}>
      {contextHolder}
      <div className={`${styles.toolbarLeft} ${isTaskPage ? '!opacity-0': ''}`}>
        <Popover  
          content={filterCardContent}
          open={state.activePopover === 'filter'}
          onOpenChange={(open) => {
            if (open) showPopover('filter');
            else if (state.activePopover === 'filter') closePopover();
          }}
          trigger="click"
          placement="bottomLeft"
          overlayClassName="filter-popover"
          destroyOnHidden
        >
          <Button
            icon={<FilterOutlined />}
            className={styles.filterButton}
            onClick={() => showPopover('filter')}
          >
            Filter <span className="filter-count">{getFilterCount()}</span>
          </Button>
        </Popover>

        {/* <Popover
          content={groupCardContent}
          visible={state.activePopover === 'group'}
          onVisibleChange={(visible) => {
            if (visible) showPopover('group');
            else if (state.activePopover === 'group') closePopover();
          }}
          trigger="click"
          placement="bottomLeft"
          overlayClassName="filter-popover"
          destroyTooltipOnHide
        >
          <Button
            icon={<GroupOutlined />}
            className={styles.groupButton}
            onClick={() => showPopover('group')}
          >
            Group <span className="group-count">{getGroupCount()}</span>
          </Button>
        </Popover> */}

        <Popover
          content={sortCardContent}
          open={state.activePopover === 'sort'}
          onOpenChange={(open) => {
            if (open) showPopover('sort');
            else if (state.activePopover === 'sort') closePopover();
          }}
          trigger="click"
          placement="bottomLeft"
          overlayClassName="filter-popover"
          destroyOnHidden
        >
          <Button
            icon={<SortIcon />}
            className={styles.sortButton}
            onClick={() => showPopover('sort')}
          >
            Sort{' '}
            {getSortCount() > 0 && (
              <span className="filter-count">{getSortCount()}</span>
            )}
          </Button>
        </Popover>
      </div>

      <div className={styles.toolbarRight} ref={buttonContainerRef}>
      </div>

      {/* 创建任务模态框 */}
      <CreateTaskModal
        visible={isCreateTaskModalVisible}
        onCancel={handleModalCancel}
      />

      {/* 导入任务模态框 */}
      <ImportTaskModal
        visible={isImportTaskModalVisible}
        onCancel={handleImportModalCancel}
        onImport={handleImportSuccess}
      />

      {/* viewport‐preserve 弹窗 */}
      {showViewportModal && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div
            className="absolute top-16 right-48 bg-white rounded-2xl shadow-lg p-3 w-60 pointer-events-auto"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isSubmenuPage ? (
              <>
                <p className="text-gray-700 text-sm mb-4">
                  要将修改保存到原视图吗？
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewportModal(false);
                    }}
                    className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    不保存
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveOriginal();
                      setShowViewportModal(false);
                    }}
                    className="px-3 py-1 text-sm text-green-500 rounded hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    保存
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-700 text-sm mb-4">
                  喜欢这个视图吗？
                  <br />
                  立即保存，方便快速访问
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewportModal(false);
                    }}
                    className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    不保存
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                      setShowViewportModal(false);
                    }}
                    className="px-3 py-1 text-sm text-[var(--color-primary)] rounded hover:bg-[var(--color-chat-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  >
                    保存视图
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSystem;
